import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import Modal from './Modal';

interface MembershipFormProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  postcode: string;
  city: string;
  kvk: string;
  agreement: boolean;
}

const formLabels = {
  name: 'Naam',
  company: 'Bedrijf',
  email: 'E-mail',
  phone: 'Telefoonnummer',
  address: 'Adres',
  postcode: 'Postcode',
  city: 'Plaats',
  kvk: 'KVK-nummer',
  agreement: 'Aanbod geaccepteerd'
};

const MembershipForm: React.FC<MembershipFormProps> = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    company: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    city: '',
    kvk: '',
    agreement: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const mailContent = `
Naam: ${formData.name}
E-mail: ${formData.email}
Telefoonnummer: ${formData.phone}
${formData.company ? `Bedrijf: ${formData.company}` : ''}
${formData.address ? `Adres: ${formData.address}` : ''}
${formData.postcode ? `Postcode: ${formData.postcode}` : ''}
${formData.city ? `Plaats: ${formData.city}` : ''}
${formData.kvk ? `KVK-nummer: ${formData.kvk}` : ''}
Aanbod geaccepteerd: ${formData.agreement ? 'Ja' : 'Nee'}
      `.trim();

      const templateParams = {
        mailContent: mailContent,
        customerMail: formData.email
      };

      await emailjs.send(
        'service_ab74jwr',
        'template_dk38pij',
        templateParams,
        'cO9IDwMo-d21soBmv'
      );

      setSuccess(true);
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        address: '',
        postcode: '',
        city: '',
        kvk: '',
        agreement: false
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Er is een fout opgetreden bij het versturen van het formulier. Probeer het later opnieuw.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div>
        <h2 className="text-2xl font-semibold text-[#5A130E] mb-6 text-center">Word lid van Sisterhood Memberclub</h2>
        {success ? (
          <div className="text-center text-[#5A130E]">
            <p className="mb-4">Bedankt voor je aanmelding! We nemen zo spoedig mogelijk contact met je op.</p>
            <button
              onClick={onClose}
              className="button"
            >
              Sluiten
            </button>
          </div>
        ) : (
          <form
            className="space-y-6 text-left"
            onSubmit={handleSubmit}>

            {/* Required fields */}
            <div>
              <label className="block text-sm font-medium text-[#5A130E] mb-1">
                {formLabels.name}*
              </label>
              <input
                type="text"
                name={formLabels.name}
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                placeholder="Jouw volledige naam"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5A130E] mb-1">
                {formLabels.email}*
              </label>
              <input
                type="email"
                required
                name={formLabels.email}
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                placeholder="jouw@email.nl"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#5A130E] mb-1">
                {formLabels.phone}*
              </label>
              <input
                type="tel"
                required
                name={formLabels.phone}
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                placeholder="+31 6 12345678"
              />
            </div>

            {/* Required fields indicator */}
            <p className="text-sm text-gray-600 italic">* verplicht veld</p>

            {/* Optional business invoice fields */}
            <div className="pt-6">
              <p className="text-sm text-gray-600 mb-4 italic">
                Vul onderstaande velden alleen in als je een factuur op bedrijfsnaam wilt ontvangen.
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#5A130E] mb-1">
                    {formLabels.company}
                  </label>
                  <input
                    type="text"
                    name={formLabels.company}
                    value={formData.company}
                    onChange={(e) => setFormData(prev => ({ ...prev, company: e.target.value }))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                    placeholder="Jouw bedrijfsnaam"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#5A130E] mb-1">
                    {formLabels.address}
                  </label>
                  <input
                    type="text"
                    name={formLabels.address}
                    value={formData.address}
                    onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                    placeholder="Straatnaam en huisnummer"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#5A130E] mb-1">
                      {formLabels.postcode}
                    </label>
                    <input
                      type="text"
                      name={formLabels.postcode}
                      value={formData.postcode}
                      onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                      placeholder="1234 AB"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#5A130E] mb-1">
                      {formLabels.city}
                    </label>
                    <input
                      type="text"
                      name={formLabels.city}
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                      placeholder="Plaatsnaam"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#5A130E] mb-1">
                    {formLabels.kvk}
                  </label>
                  <input
                    type="text"
                    name={formLabels.kvk}
                    value={formData.kvk}
                    onChange={(e) => setFormData(prev => ({ ...prev, kvk: e.target.value }))}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-[#E37C64] focus:ring-[#E37C64]"
                    placeholder="KVK-nummer"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="membership-agreement"
                name={formLabels.agreement}
                checked={formData.agreement}
                onChange={(e) => setFormData(prev => ({ ...prev, agreement: e.target.checked }))}
                className="mt-1.5 rounded border-gray-300 text-[#E37C64] focus:ring-[#E37C64]"
                required
              />
              <label htmlFor="membership-agreement" className="text-sm text-[#5A130E]">
                <strong>Ja, ik word lid voor een jaar.</strong> Ik betaal €799,- (excl. BTW) voor één jaar lidmaatschap van de Sisterhood Memberclub.
              </label>
            </div>
            <div className="flex justify-between pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 bg-gray-100 rounded-full"
              >
                Sluiten
              </button>
              <button 
                type="submit" 
                className="button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Versturen...' : 'Versturen'}
              </button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default MembershipForm;